import React, { useEffect, useRef, useState } from 'react';
import { Modal, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import PayrollConnectionEventList from './PayrollConnectionEventList';
import styles from './styles/payroll-connections.module.scss';

/**
 * The full connection history for one payroll connection.
 *
 * The card body only ever shows the newest few events, because a member with
 * a repeatedly-relinked job has dozens and the panel sits on a 33% rail. This
 * is where the whole record lives: every event loaded so far, newest first,
 * paged in by scrolling rather than by a button — a record read top-to-bottom
 * should not stop every page to ask permission to continue.
 *
 * Paging state is NOT owned here. `items`, `pageinfo` and `loadingMore` come
 * from the page controller's per-connection slice, so pages fetched from
 * inside the modal survive closing it — reopening shows everything already
 * loaded instead of starting over at page 1.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array<Object>} props.items Events loaded so far, newest first.
 * @param {{ page: number, count?: number, hasNext: boolean } | null} [props.pageinfo]
 * @param {boolean} [props.loadingMore] A page is in flight.
 * @param {Object | null} [props.moreError] The last page request failed.
 * @param {() => void} [props.onLoadMore] Doubles as the retry for `moreError`.
 * @param {string} [props.timezone] Declared zone from the payload.
 * @param {string} [props.connectionLabel] Platform name, for the modal title.
 */
function PayrollConnectionHistoryModal({
    open,
    onClose,
    items,
    pageinfo = null,
    loadingMore = false,
    moreError = null,
    onLoadMore,
    timezone,
    connectionLabel,
}) {
    // The rows scroll, not the dialog, so the sentinel has to be observed
    // against this element — against the viewport it would sit permanently
    // out of view and never fire. State, not the ref itself: the observer is
    // installed on a prop change, and a ref mutation is not one.
    const listRef = useRef(null);
    const [scrollRoot, setScrollRoot] = useState(null);

    useEffect(() => {
        setScrollRoot(listRef.current);
    }, [open]);

    const loaded = Array.isArray(items) ? items.length : 0;
    // `count` is the endpoint's filtered total, so "of 83" stays honest even
    // though only the first pages are in memory. It is absent on older
    // payloads, in which case the caption just states what is on screen.
    const total = Number(pageinfo?.count);
    const hasTotal = Number.isFinite(total) && total > 0;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title={connectionLabel ? `${connectionLabel} · Connection history` : 'Connection history'}
        >
            <div className={styles.modalContent}>
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.modalCount}>
                    {hasTotal && total > loaded
                        ? `Showing ${loaded} of ${total} events`
                        : `${loaded === 1 ? '1 event' : `${loaded} events`}`}
                </Text>

                <div ref={listRef} className={styles.modalList}>
                    <PayrollConnectionEventList
                        items={items}
                        timezone={timezone}
                        ariaLabel="Connection history"
                        heading="All events"
                        hasMore={!!pageinfo?.hasNext}
                        loadingMore={loadingMore}
                        moreError={moreError}
                        onLoadMore={onLoadMore}
                        scrollRoot={scrollRoot}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default PayrollConnectionHistoryModal;
